import { apiRequest, getAdpApiClient } from "../api/client"
import type { CreateDataFlowParams, CreateDataFlowResponse, CreateDataFlowScheduleRequest, DataFlowMappingRequest, PipelineHistoryParams, PipelineHistoryResponse, PipelineItem, PipelineListParams, PipelineListResponse, PipelineLogsParams, PipelineLogsResponse, UpdateDataFlowRequest, UpdateDataFlowResponse } from "./pipeline.types"

export const pipelineService = {
    createDataFlow: async (params: CreateDataFlowParams): Promise<CreateDataFlowResponse> => {
        return await apiRequest<CreateDataFlowResponse>({
            method: 'POST',
            url: '/data-flows',
            data: params,
        }, 'Failed to create data flow');
    },

    updateDataFlow: async (dataFlowId: string, params: UpdateDataFlowRequest): Promise<UpdateDataFlowResponse> => {
        return await apiRequest<UpdateDataFlowResponse>({
            method: 'PUT',
            url: `/data-flows/${dataFlowId}`,
            data: params,
        }, 'Failed to update data flow');
    },

    dataFlowMapping: async (dataFlowId: string, params: DataFlowMappingRequest): Promise<unknown> => {
        return await apiRequest<unknown>({
            method: 'POST',
            url: `/data-flows/${dataFlowId}/mapping`,
            data: params,
        }, 'Failed to map data flow');
    },

    runImmediateJob: async (sessionId: string): Promise<unknown> => {
        return await apiRequest<unknown>({
            method: 'POST',
            url: `/etl-jobs/${sessionId}/run-immediate`,
        }, 'Failed to run pipeline');
    },

    runJob: async (sessionId: string): Promise<unknown> => {
        return await apiRequest<unknown>({
            method: 'POST',
            url: `/etl-jobs/${sessionId}/run`,
        }, 'Failed to run pipeline');
    },

    killJob: async (sessionId: string): Promise<unknown> => {
        return await apiRequest<unknown>({
            method: 'POST',
            url: `/etl-jobs/${sessionId}/kill`,
        }, 'Failed to kill pipeline');
    },

    fetchPipelineList: async (params: PipelineListParams): Promise<PipelineListResponse> => {
        const mappedParams = Object.fromEntries(
            Object.entries({
                page_number: params.page,
                limit: params.size,
                pipeline_name: params.search,
                filterStatus: params.status,
            }).filter(([_, v]) => v !== undefined && v !== '')
        );

        const data = await apiRequest<{ result: PipelineItem[], searchCount: number }>({
            method: 'GET',
            url: '/data-flows',
            params: mappedParams,
        }, 'Failed to fetch pipeline list');

        return {
            pipelines: data.result,
            totalCount: data.searchCount,
        }
    },

    createDataFlowSchedule: async (dataFlowId: number, params: CreateDataFlowScheduleRequest): Promise<unknown> => {
        return await apiRequest<unknown>({
            method: 'POST',
            url: `/data-flow-schedulers/${dataFlowId}`,
            data: params,
        }, 'Failed to create data flow schedule');
    },

    fetchPipelineHistory: async (params: PipelineHistoryParams): Promise<PipelineHistoryResponse> => {
        return await apiRequest<PipelineHistoryResponse>({
            method: 'GET',
            url: '/data-flows/pipeline-history',
            params,
        }, 'Failed to fetch pipeline history');
    },

    /**
     * The logs endpoint does not use the envelope — bypasses `apiRequest` and
     * reads the response shape directly. Errors still surface a toast via the
     * shared axios response interceptor on `getAdpApiClient`.
     */
    fetchPipelineLogs: async (params: PipelineLogsParams): Promise<PipelineLogsResponse> => {
        const { mode, ...rest } = params;
        const queryParams = mode ? { ...rest, mode } : rest;
        const response = await getAdpApiClient().get<PipelineLogsResponse>('/etl-jobs/logs', {
            params: queryParams,
        });
        return response.data;
    },
}